import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    CircularProgress,
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    CheckCircle,
    Cancel,
} from '@mui/icons-material';
import { companyAPI } from '../../services/api';

const Companies = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        max_users: 1
    });

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const response = await companyAPI.getAll();
            setCompanies(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to load companies');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (company = null) => {
        if (company) {
            setEditingCompany(company);
            setFormData({
                name: company.name,
                max_users: company.max_users
            });
        } else {
            setEditingCompany(null);
            setFormData({
                name: '',
                max_users: 1
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingCompany(null);
        setFormData({
            name: '',
            max_users: 1
        });
    };

    const handleSubmit = async () => {
        try {
            if (editingCompany) {
                await companyAPI.update(editingCompany.id, formData);
            } else {
                await companyAPI.create(formData);
            }
            handleCloseDialog();
            fetchCompanies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save company');
        }
    };

    const handleUpdateUserLimit = async (companyId, newLimit) => {
        try {
            await companyAPI.updateUserLimit(companyId, newLimit);
            fetchCompanies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user limit');
        }
    };

    const handleToggleStatus = async (companyId) => {
        try {
            await companyAPI.toggleStatus(companyId);
            fetchCompanies();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to toggle status');
        }
    };

    const handleDelete = async (companyId) => {
        if (window.confirm('Are you sure you want to delete this company?')) {
            try {
                await companyAPI.delete(companyId);
                fetchCompanies();
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete company');
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight={600}>
                    Companies Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Company
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Company Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Users</TableCell>
                            <TableCell>Max Users</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {companies.map((company) => (
                            <TableRow key={company.id} hover>
                                <TableCell>
                                    <Typography fontWeight={500}>{company.company_name}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        icon={company.is_active ? <CheckCircle /> : <Cancel />}
                                        label={company.is_active ? 'Active' : 'Inactive'}
                                        color={company.is_active ? 'success' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {company.current_users || 0}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography>{company.max_users}</Typography>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                const newLimit = prompt('Enter new user limit:', company.max_users);
                                                if (newLimit && !isNaN(newLimit)) {
                                                    handleUpdateUserLimit(company.id, parseInt(newLimit));
                                                }
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {new Date(company.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenDialog(company)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color={company.is_active ? 'error' : 'success'}
                                        onClick={() => handleToggleStatus(company.id)}
                                    >
                                        {company.is_active ? <Cancel /> : <CheckCircle />}
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(company.id)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCompany ? 'Edit Company' : 'Add New Company'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Company Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Max Users"
                        type="number"
                        value={formData.max_users}
                        onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                        margin="normal"
                        required
                        inputProps={{ min: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingCompany ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Companies;
